import React, { useMemo, useState } from 'react';
import { useI18n } from '../../../contexts/I18nContext';

const resolveLabel = (value, options) => {
  if (!value) return '';
  const match = Array.isArray(options) ? options.find((opt) => opt.value === value) : null;
  return match?.label || value;
};

const buildSummary = ({ model, videoMode, aspectRatio, videoResolution, videoDuration }, optionSets) => {
  const modeEnabled = Array.isArray(optionSets.videoModeOptions) && optionSets.videoModeOptions.length > 0;
  const items = [
    resolveLabel(model, optionSets.modelOptions),
    modeEnabled ? resolveLabel(videoMode, optionSets.videoModeOptions) : '',
    resolveLabel(aspectRatio, optionSets.aspectRatioOptions),
    resolveLabel(videoResolution, optionSets.videoResolutionOptions),
    resolveLabel(videoDuration, optionSets.videoDurationOptions)
  ].filter(Boolean);
  return items;
};

const VideoOptionsSelector = ({
  title,
  model,
  videoMode,
  aspectRatio,
  videoResolution,
  videoDuration,
  modelOptions,
  videoModeOptions,
  aspectRatioOptions,
  videoResolutionOptions,
  videoDurationOptions,
  onModelChange,
  onVideoModeChange,
  onAspectRatioChange,
  onVideoResolutionChange,
  onVideoDurationChange
}) => {
  const { t, language } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const summary = useMemo(
    () => buildSummary(
      { model, videoMode, aspectRatio, videoResolution, videoDuration },
      { modelOptions, videoModeOptions, aspectRatioOptions, videoResolutionOptions, videoDurationOptions }
    ),
    [model, videoMode, aspectRatio, videoResolution, videoDuration, modelOptions, videoModeOptions, aspectRatioOptions, videoResolutionOptions, videoDurationOptions]
  );

  const labels = useMemo(() => {
    if (language === 'en') {
      return { mode: 'Mode', resolution: 'Resolution', duration: 'Duration', expand: 'Expand', collapse: 'Collapse', placeholder: 'Please select options' };
    }
    if (language === 'zh-TW') {
      return { mode: '模式', resolution: '解析度', duration: '時長', expand: '展開', collapse: '收起', placeholder: '請選擇配置' };
    }
    return { mode: '模式', resolution: '分辨率', duration: '时长', expand: '展开', collapse: '收起', placeholder: '请选择配置' };
  }, [language]);

  const renderOptions = (options, value, onChange) => {
    if (!Array.isArray(options) || options.length === 0) return null;
    return (
      <div className="veo2-row veo2-row--wrap">
        {options.map((option) => (
          <button
            key={option.value}
            className={`veo2-pill ${value === option.value ? 'is-active' : ''}`}
            onClick={() => onChange(option.value)}
          >
            {option.label || option.value}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="veo2-section">
      <div className="veo2-options__bar">
        <div className="veo2-section__title">{title}</div>
        <button className="veo2-options__action" onClick={() => setIsOpen((prev) => !prev)}>
          <span>{isOpen ? labels.collapse : labels.expand}</span>
          <span className={`veo2-options__chevron ${isOpen ? 'is-open' : ''}`} />
        </button>
      </div>
      <div className="veo2-divider" />
      <div className={`veo2-options ${isOpen ? 'is-open' : ''}`}>
        <button className="veo2-options__summary-btn" onClick={() => setIsOpen((prev) => !prev)}>
          <div className="veo2-options__summary">
            {summary.length > 0 ? summary.map((item) => (
              <span key={item} className="veo2-options__chip veo2-options__chip--active">{item}</span>
            )) : <span className="veo2-options__placeholder">{labels.placeholder}</span>}
          </div>
        </button>
        {isOpen && (
          <div className="veo2-options__content">
            <div className="veo2-section">
              <div className="veo2-section__title">{t('videoGeneration.veo.common.model')}</div>
              <div className="veo2-divider" />
              {renderOptions(modelOptions, model, onModelChange)}
            </div>
            {Array.isArray(videoModeOptions) && videoModeOptions.length > 0 && (
              <div className="veo2-section">
                <div className="veo2-section__title">{labels.mode}</div>
                <div className="veo2-divider" />
                {renderOptions(videoModeOptions, videoMode, onVideoModeChange)}
              </div>
            )}
            <div className="veo2-section">
              <div className="veo2-section__title">{t('videoGeneration.veo.common.aspectRatio')}</div>
              <div className="veo2-divider" />
              {renderOptions(aspectRatioOptions, aspectRatio, onAspectRatioChange)}
            </div>
            <div className="veo2-section">
              <div className="veo2-section__title">{labels.resolution}</div>
              <div className="veo2-divider" />
              {renderOptions(videoResolutionOptions, videoResolution, onVideoResolutionChange)}
            </div>
            <div className="veo2-section">
              <div className="veo2-section__title">{labels.duration}</div>
              <div className="veo2-divider" />
              {renderOptions(videoDurationOptions, videoDuration, onVideoDurationChange)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoOptionsSelector;
